import type { ParkingRecord } from '@/services/dto';
import ParkingResultCard from './ParkingResultCard';
import { useConfigStore } from '@/stores';
import { useTranslation } from 'react-i18next';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTickSoundInteraction } from '@/hooks';

interface SearchResultsListProps {
    results: ParkingRecord[];
    onCardClick: (result: ParkingRecord) => void;
}

export interface SearchResultsListRef {
    focusHeader: () => void;
}

const SearchResultsList = forwardRef<SearchResultsListRef, SearchResultsListProps>(({ results, onCardClick }, ref) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const { playTick } = useTickSoundInteraction();

    useImperativeHandle(ref, () => ({
        focusHeader: () => {
            headerRef.current?.focus();
        },
    }));
    const { config } = useConfigStore();
    const { screenMode } = config.ui;
    const { t } = useTranslation();
    if (results.length === 0) {
        return null;
    }

    return (
        <div className={`space-y-[calc(40*var(--vh-unit))] ${screenMode !== 'fullscreen' ? 'h-[calc(572*var(--vh-unit))]' : ''}`}>
            {/* Header */}
            <div className="text-center">
                <h2
                    ref={headerRef}
                    data-region-nav
                    data-region-order={2}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            playTick();
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                    tabIndex={2}
                    data-interactive
                    aria-label={t('parking.searchResultsList.ariaLabel')}
                    className="focus-border widescreen:w-[calc(697*var(--vw-unit))] widescreen:text-[calc(22*var(--vh-unit))] widescreen:font-medium widescreen:leading-[calc(32*var(--vh-unit))] widescreen:tracking-[calc(-0.44*var(--vh-unit))] h-[calc(64*var(--vh-unit))] w-[calc(360*var(--vw-unit))] rounded-[calc(12*var(--vh-unit))] border-[calc(1*var(--vh-unit))] border-[var(--color-border-transparent)] bg-[var(--color-eaeaea-to-black-222)] px-[calc(24*var(--vw-unit))] py-[calc(16*var(--vh-unit))] text-[calc(20*var(--vh-unit))] leading-[calc(32*var(--vh-unit))] font-bold tracking-[calc(-0.4*var(--vh-unit))] text-[var(--color-text-black)]"
                >
                    {t('parking.searchResultsList.header')}
                </h2>
            </div>

            {/* Display all license plates */}
            <div className="widescreen:flex widescreen:flex-col widescreen:gap-[calc(40*var(--vh-unit))] widescreen:overflow-auto fullscreen:widescreen:h-[calc(506*var(--vh-unit))] widescreen:h-[calc(366*var(--vh-unit))] scrollbar-custom-gray-99938f scrollbar-w-4 flex flex-col gap-[calc(40*var(--vh-unit))]">
                {results.map((result, index) => (
                    <div className="widescreen:mr-[calc(40*var(--vw-unit))] mr-[calc(36*var(--vw-unit))]" data-region-nav data-region-order={3}>
                        <ParkingResultCard key={index} result={result} onClick={onCardClick} totalResults={results.length} />
                    </div>
                ))}
            </div>
        </div>
    );
});

SearchResultsList.displayName = 'SearchResultsList';
export default SearchResultsList;
